import React, { useMemo, useCallback } from "react";
import Popover from "components/common/Popover";
import SearchFilterForm from "search-filter/components/search-filter-bar/SearchFilterForm";
import useUserSearchFilterMutation from "search-filter/hooks/useUserSearchFilterMutation";
import { Spinner, Link } from "components/common";
import { REFERENCED_USER_SEARCH_FILTER_TYPE, INLINE_SEARCH_FILTER_TYPE } from "search-filter/constants";
import useSearchFilterUsagesSummaryQuery from "search-filter/hooks/useSearchFilterUsagesSummaryQuery";
import readableFilterUsagesSummary from "search-filter/readableFilterUsagesSummary";
import StringUtils from "util/StringUtils";
import confirmActionUsagesAlert from "search-filter/confirmActionUsagesAlert";
import useView from "search-filter/hooks/useView";
import SearchFiltersRoutes from "search-filter/Routes";
const excludeCurrentViewFromSummary = ({
  usagesSummary,
  isSavedView,
  viewType
}) => {
  if (!isSavedView) {
    return usagesSummary;
  }
  return usagesSummary.map(({ usages, type }) => {
    if (type.toLowerCase() === (viewType == null ? void 0 : viewType.toLowerCase())) {
      return { type, usages: usages - 1 };
    }
    return { type, usages };
  }).filter(({ usages }) => usages > 0);
};
const UsagesWarning = ({
  filterId,
  filterType,
  usagesSummaryExcludingCurrentView,
  isFetchingUsagesSummary
}) => {
  if (filterType !== REFERENCED_USER_SEARCH_FILTER_TYPE) {
    return null;
  }
  if (isFetchingUsagesSummary) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: "Loading usages.." });
  }
  return /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement("i", null, !(usagesSummaryExcludingCurrentView == null ? void 0 : usagesSummaryExcludingCurrentView.length) && "Please note, changes to this filter will be persisted. This filter is currently not being referenced in any other saved search or dashboard.", !!(usagesSummaryExcludingCurrentView == null ? void 0 : usagesSummaryExcludingCurrentView.length) && /* @__PURE__ */ React.createElement(React.Fragment, null, "Important! This search filter is also being referenced by", " ", /* @__PURE__ */ React.createElement(Link, { to: SearchFiltersRoutes.SHOW(filterId), target: "_blank" }, readableFilterUsagesSummary(usagesSummaryExcludingCurrentView)), ". Changing the search filter query will affect", " ", StringUtils.pluralize(usagesSummaryExcludingCurrentView.length, "its", "their"), " search result.")));
};
const useUsagesSummaryExcludingCurrentView = (filterId, filterType) => {
  const view = useView();
  const {
    data: { summary: usagesSummary },
    isFetching: isFetchingUsagesSummary
  } = useSearchFilterUsagesSummaryQuery(filterId, { enabled: filterType === REFERENCED_USER_SEARCH_FILTER_TYPE });
  const isSavedView = !!(view == null ? void 0 : view.id);
  const usagesSummaryExcludingCurrentView = excludeCurrentViewFromSummary({
    usagesSummary,
    isSavedView,
    viewType: view == null ? void 0 : view.type
  });
  return { usagesSummaryExcludingCurrentView, isFetchingUsagesSummary };
};
const SearchFilterEditPopover = ({
  children = void 0,
  onSave,
  onCancel,
  filter: { id, frontendId, title, description, queryString, negation, type }
}) => {
  const { put } = useUserSearchFilterMutation();
  const { usagesSummaryExcludingCurrentView, isFetchingUsagesSummary } = useUsagesSummaryExcludingCurrentView(id, type);
  const handleFormSubmit = useCallback(
    (filterData, checked) => {
      if (checked) {
        return onSave({ ...filterData, frontendId, negation, type: INLINE_SEARCH_FILTER_TYPE });
      }
      if (type === INLINE_SEARCH_FILTER_TYPE) {
        return onSave({ ...filterData, id, frontendId, negation, type: INLINE_SEARCH_FILTER_TYPE });
      }
      if ((usagesSummaryExcludingCurrentView == null ? void 0 : usagesSummaryExcludingCurrentView.length) && filterData.queryString !== queryString && !confirmActionUsagesAlert({ action: "Changing", usagesSummary: usagesSummaryExcludingCurrentView })) {
        return Promise.resolve();
      }
      return put({ data: filterData, id }).then(() => {
        onSave({ ...filterData, negation, id, frontendId, type: REFERENCED_USER_SEARCH_FILTER_TYPE });
      });
    },
    [frontendId, id, negation, onSave, put, queryString, usagesSummaryExcludingCurrentView, type]
  );
  const initialValues = useMemo(() => ({ title, description, queryString }), [description, queryString, title]);
  return /* @__PURE__ */ React.createElement(Popover, { onClose: onCancel, opened: true, width: 460, withArrow: true, withinPortal: true }, /* @__PURE__ */ React.createElement(Popover.Target, null, children), /* @__PURE__ */ React.createElement(Popover.Dropdown, { title: "Edit Filter", id: "edit-search-filter-dropdown" }, /* @__PURE__ */ React.createElement(
    SearchFilterForm,
    {
      onCancel,
      onSubmit: handleFormSubmit,
      showCheckbox: type === REFERENCED_USER_SEARCH_FILTER_TYPE,
      checkboxLabel: "Create copy for current search",
      helpText: 'This action will create a copy of this filter and remove the reference to the filter saved in "My Filters".',
      helpTitle: "Create copy",
      formDescription: /* @__PURE__ */ React.createElement(
        UsagesWarning,
        {
          filterId: id,
          filterType: type,
          usagesSummaryExcludingCurrentView,
          isFetchingUsagesSummary
        }
      ),
      initialValues,
      submitButtonText: "Update filter",
      submitLoadingText: "Updating Filter..."
    }
  )));
};
export default SearchFilterEditPopover;
